/**
 * Zar overlay'i — tiklanan her sayi icin d20 animasyonu.
 *
 * Akis: 0ms overlay acilir -> zar 1150ms donerek yuvarlanir -> sonuc 220ms
 * ile yerine oturur -> 2400ms sonra kendini kapatir. Kullanici herhangi bir
 * an tiklayarak kapatabilir.
 */

import { useEffect, useRef, useState } from 'react'
import { useRollStore } from './rollStore'

// NOT: Bu dosya YALNIZCA React bileşenleri export eder. Bileşen olmayan bir
// değeri (ör. `signed`) buradan yeniden export etmek Vite Fast Refresh'i
// bozar ("export is incompatible" uyarısı). `signed` zaten `rollStore`'dan
// geliyor; `index.ts` her ikisini de doğru yerlerden toplar.

const AUTO_DISMISS_MS = 2400

/** Stilize d20 (icosahedron) cizimi. */
function D20Shape({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <polygon
        points="50,3 92,26.5 92,73.5 50,97 8,73.5 8,26.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
      <polygon points="50,29 68,58 32,58" fill="none" stroke="currentColor" strokeWidth="3" />
      <g stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" opacity="0.75">
        <line x1="50" y1="3" x2="50" y2="29" />
        <line x1="92" y1="26.5" x2="68" y2="58" />
        <line x1="92" y1="73.5" x2="68" y2="58" />
        <line x1="8" y1="73.5" x2="32" y2="58" />
        <line x1="8" y1="26.5" x2="32" y2="58" />
        <line x1="50" y1="97" x2="50" y2="70" />
      </g>
    </svg>
  )
}

export function DiceOverlay() {
  const queue = useRollStore((s) => s.queue)
  const dismiss = useRollStore((s) => s.dismiss)
  const current = queue[0] ?? null
  const [phase, setPhase] = useState<'tumbling' | 'settled'>('tumbling')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!current) return
    setPhase('tumbling')
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setPhase('settled'), 1150)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [current])

  useEffect(() => {
    if (!current) return
    const t = setTimeout(() => dismiss(current.id), AUTO_DISMISS_MS)
    return () => clearTimeout(t)
  }, [current, dismiss])

  // Klavye: herhangi bir tus overlay'i kapatir
  useEffect(() => {
    if (!current) return
    const onKey = () => dismiss(current.id)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, dismiss])

  if (!current) return null

  const settled = phase === 'settled'
  const d20 = current.d20
  const isCrit = d20?.isCritical ?? false
  const isFumble = d20?.isFumble ?? false
  const tone = isCrit ? 'text-gold-bright' : isFumble ? 'text-blood' : 'text-gold'

  return (
    <div className="animate-overlay-in fixed inset-0 z-50 flex items-center justify-center">
      {/* Kapatma katmani: gercek bir <button> — hem fare hem klavye erisimi */}
      <button
        type="button"
        aria-label="Zar sonucunu kapat"
        onClick={() => dismiss(current.id)}
        className="absolute inset-0 cursor-default bg-black/70 backdrop-blur-sm"
      />
      <div className="animate-pop-in relative flex flex-col items-center gap-4 px-6">
        {/* Zar */}
        <div
          className={[
            'relative grid h-32 w-32 place-items-center rounded-full',
            isCrit ? 'die-crit text-gold-bright' : '',
            isFumble ? 'die-fumble text-blood' : '',
          ].join(' ')}
        >
          <D20Shape
            className={[
              'absolute inset-0 h-full w-full',
              settled ? 'die-settling' : 'die-tumbling',
            ].join(' ')}
          />
          <span
            className={`tnum font-display text-5xl font-bold ${
              settled ? 'animate-pop-in' : 'opacity-0'
            } ${tone}`}
          >
            {d20?.die ?? '—'}
          </span>
        </div>

        {/* Etiket */}
        <div className="text-center">
          <div className="font-display text-lg text-ink">{current.label}</div>
          <div className="tnum text-sm text-ink-muted">{current.subtitle}</div>
        </div>

        {/* Sonuc */}
        {settled && (
          <div className="animate-pop-in flex flex-col items-center gap-1">
            {d20 && (
              <div className="tnum font-display text-4xl font-bold text-ink">{d20.total}</div>
            )}

            {d20?.mode !== 'normal' && d20 && (
              <div className="tnum text-xs text-ink-faint">atılan zar: {d20.rolls.join(' , ')}</div>
            )}

            {isCrit && <div className="text-sm font-semibold text-gold-bright">KRİT!</div>}
            {isFumble && <div className="text-sm font-semibold text-blood">KRİT BAŞARISIZ!</div>}

            {current.outcome && (
              <div
                className={`tnum text-sm font-semibold ${
                  current.outcome.success ? 'text-vital' : 'text-blood'
                }`}
              >
                DC {current.outcome.dc} ·{' '}
                {current.outcome.grade === 'critical-success'
                  ? 'KRİT BAŞARI'
                  : current.outcome.grade === 'fumble'
                    ? 'KRİT BAŞARISIZ'
                    : current.outcome.success
                      ? 'BAŞARILI'
                      : 'BAŞARISIZ'}
                {current.outcome.margin !== 0 && (
                  <span className="text-ink-faint">
                    {' '}
                    ({current.outcome.margin > 0 ? '+' : ''}
                    {current.outcome.margin})
                  </span>
                )}
              </div>
            )}

            {current.attack && (
              <div className="mt-1 flex flex-col items-center gap-1 text-center">
                {current.attack.targetAc != null && current.attack.hit != null && (
                  <div
                    className={`text-sm font-semibold ${
                      current.attack.hit ? 'text-vital' : 'text-blood'
                    }`}
                  >
                    {current.attack.hit ? 'ISKET' : 'ISKA'}
                    {current.attack.targetAc != null && ` (hedef AC ${current.attack.targetAc})`}
                  </div>
                )}
                {current.formula && current.attack.hit !== false && (
                  <div className="tnum text-gold-bright">
                    {current.attack.damageTotal} hasar{' '}
                    <span className="text-ink-muted">({current.attack.damageType})</span>
                  </div>
                )}
                {current.attack.targetAc == null && current.formula && (
                  <div className="tnum text-gold-bright">
                    {current.formula.total} <span className="text-ink-muted">{current.label}</span>
                  </div>
                )}
              </div>
            )}

            {!current.attack && current.formula && (
              <div className="tnum text-gold-bright">{current.formula.summary}</div>
            )}
          </div>
        )}

        <div className="text-[0.65rem] text-ink-faint">kapatmak için tıkla</div>
      </div>
    </div>
  )
}

/**
 * Zar gecmisi paneli (sag alt kose).
 *
 * ONEMLI: Gecmis, overlay kuyrugundan AYRI tutulur. Aksi halde overlay
 * kapaninca gecmis de kaybolur ve kullanici atislarini hicbir zaman
 * goremez. Bu panel her zaman erisilebilir olmalidir — masada "ne attin
 * ki?" sorusu en sik sorulan sorudur.
 */
export function RollHistory() {
  const history = useRollStore((s) => s.history)
  const clearHistory = useRollStore((s) => s.clearHistory)
  const [open, setOpen] = useState(false)

  return (
    <div className="fixed right-3 bottom-3 z-40 w-60">
      {open && (
        <div className="panel animate-pop-in mb-2 max-h-72 overflow-y-auto">
          <div className="panel-title flex items-center justify-between">
            <span>Zar Geçmişi ({history.length})</span>
            {history.length > 0 && (
              <button
                type="button"
                onClick={clearHistory}
                className="cursor-pointer text-ink-faint hover:text-ink"
              >
                temizle
              </button>
            )}
          </div>
          <div className="p-2">
            {history.length === 0 && (
              <div className="p-2 text-center text-xs text-ink-faint">Henüz atış yok.</div>
            )}
            {history.map((r) => {
              const value = r.d20 ? r.d20.total : (r.formula?.total ?? '—')
              const tone = r.d20?.isCritical
                ? 'text-gold-bright'
                : r.d20?.isFumble
                  ? 'text-blood'
                  : r.attack?.hit === false
                    ? 'text-blood'
                    : 'text-ink'
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-2 border-b border-line-soft py-1 last:border-0"
                >
                  <span className="truncate text-xs text-ink-muted" title={r.subtitle}>
                    {r.label}
                  </span>
                  <span className={`tnum shrink-0 text-sm font-semibold ${tone}`}>{value}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="panel ml-auto flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-ink-muted transition-colors hover:border-gold-dim hover:text-gold"
      >
        <svg viewBox="0 0 100 100" className="h-4 w-4 text-gold" aria-hidden="true">
          <polygon
            points="50,3 92,26.5 92,73.5 50,97 8,73.5 8,26.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
          />
        </svg>
        {open ? 'kapat' : `zar geçmişi${history.length > 0 ? ` (${history.length})` : ''}`}
      </button>
    </div>
  )
}
